import { Injectable } from '@angular/core';
import { calculateBoundingBox } from '@creative/elements/utils';
import { isLocked } from '@creative/nodes';
import { isHidden, toFlatElementNodeList } from '@creative/nodes/helpers';
import { IBoundingBox } from '@studio/domain';
import { combineLatest, merge, Observable } from 'rxjs';
import { filter, map, tap, withLatestFrom } from 'rxjs/operators';
import { PropertiesService } from '../properties-panel/properties.service';
import { EditorEventService } from './editor-event/editor-event.service';
import { ElementSelectionService } from './element-selection.service';

@Injectable()
export class ElementSelectionBoundingBoxService {
    boundingBox$: Observable<IBoundingBox>;
    private currentBoundingBox: IBoundingBox | undefined;
    private currentBoundingBoxExcludingLocked: IBoundingBox | undefined;

    constructor(
        private propertiesService: PropertiesService,
        private editorEventService: EditorEventService,
        private elementSelectionService: ElementSelectionService
    ) {
        const selectionElementChange$ = this.editorEventService.elements.immediateChange$.pipe(
            withLatestFrom(this.elementSelectionService.change$),
            filter(([value, selection]) => Boolean(value.element && selection.has(value.element))),
            map(([_, selection]) => selection)
        );

        this.boundingBox$ = combineLatest([
            this.propertiesService.selectedStateChange$,
            merge(elementSelectionService.change$, selectionElementChange$)
        ]).pipe(
            map(([state, selection]) => {
                const notHiddenElements = [...selection.elements].filter(element => !isHidden(element));
                const notHiddenOrLockedDirectlySelectedNodes = selection.nodes.filter(
                    node => !isHidden(node) && !isLocked(node)
                );
                const notHiddenOrLockedElements = toFlatElementNodeList(
                    notHiddenOrLockedDirectlySelectedNodes
                );
                this.currentBoundingBoxExcludingLocked = calculateBoundingBox(
                    notHiddenOrLockedElements,
                    state
                );
                return calculateBoundingBox(notHiddenElements, state);
            }),
            tap(value => (this.currentBoundingBox = value))
        );
    }

    public getCurrentBoundingBox(excludeLockedElements = false): IBoundingBox | undefined {
        return excludeLockedElements ? this.currentBoundingBoxExcludingLocked : this.currentBoundingBox;
    }
}
